import styles from './CustomQuickActionModal.module.scss';
import Modal from '../Modal';
import { useState, useMemo, useEffect } from 'react';
import { Text } from '../../typography';
import Icon from '../../Icon';
import { Form } from 'react-bootstrap';
import TypeSelectorDropdown from '../../dropdowns/TypeSelectorDropdown';
import {
  defaultAssessmentTypes,
  quickActionTypes,
} from '../../../constants/strings';
import DropdownCombo from '../../dropdowns/DropdownCombo';
import { dropdownTypes } from '../../dropdowns/dropdown';
import { useWorkspace } from '../../../providers/WorkspaceProvider';
import { useTranslation } from 'react-i18next';
import { keys } from '../../../utilities/translator/translation_keys';
import { useQuery } from '@apollo/client';
import { loader } from 'graphql.macro';
import { useMutation } from '@apollo/client';
import { useModal } from '../../../providers/ModalProvider';
import { modals } from '../../../providers/modals';
import { useTemplates } from '../../../providers/TemplateProvider';
import classNames from 'classnames';
import { useCurrentUser } from '../../../providers/UserProvider';
import Button from '../../Button';
import QuickFileDropzone from '../../image_uploads/QuickFileDropzone';
import { useQueryParams } from '../../../hooks/misc';
import Select from 'react-select';
import IconModal from './IconModal';
import { useFlags } from 'launchdarkly-react-client-sdk';

const modalQuery = loader('./CustomQuickActionModal.fetch.graphql');
const addQuickActionMutation = loader('./CustomQuickActionModal.add.graphql');
const interalFilesQuery = loader('./CustomQuickActionModal.files.graphql');

export default function CustomQuickActionModal() {
  const { disableOutsideNavigation } = useFlags();
  const { t } = useTranslation();
  const [addQuickAction] = useMutation(addQuickActionMutation);
  const { modalState, closeModal, updateModal } = useModal();
  const { assessmentName, assessmentTypes } = useCurrentUser();
  const { inWorkspaces } = useWorkspace();
  const { templates, allTemplates } = useTemplates();
  const {
    color,
    name,
    icon,
    type,
    template,
    asset,
    workspace,
    quickActionType,
    url,
    show,
    refetch,
  } = modalState.quickActionModal;

  const showTemplates = useMemo(
    () =>
      quickActionType === quickActionTypes.companyLevel
        ? allTemplates
            .filter((t) => !t.isArchived)
            ?.filter((t) => !t.workspace?.id)
            ?.filter((t) => t.templateType === type.key)
        : templates,
    [allTemplates, quickActionType, templates, type],
  );
  const { data: { files = [] } = {} } = useQuery(interalFilesQuery, {
    skip: !workspace?.id || !show,
    variables: {
      options: {
        filters: [
          {
            field: 'workspaceId',
            operator: 'or',
            value: [`${workspace?.id}`, null],
          },
          {
            field: 'url',
            operator: 'ne',
            value: [null],
          },
        ],
      },
    },
  });
  const fileOptions = useMemo(() => {
    return files?.map((f) => ({ value: f.id, label: f.name, ...f }));
  }, [files]);
  const [selectedType, setSelectedType] = useState('ASSESSMENT');
  const [page, setPage] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const { getParam } = useQueryParams();
  const [allWorkspaces, setAllWorkspaces] = useState(false);

  const string = () => {
    switch (quickActionType) {
      case quickActionTypes.workspaceLevel:
        return `This will be available to everyone in this workspace. ${
          selectedType === 'ASSESSMENT'
            ? 'Only Templates available to this Workspace can be selected.'
            : ''
        } `;
      case quickActionTypes.companyLevel:
        return `This will be available to everyone in your company. ${
          selectedType === 'ASSESSMENT'
            ? 'You will only be able to select Company Shared Templates.'
            : ''
        }`;
      default:
        return null;
    }
  };

  const onHide = () => {
    setSelectedType('ASSESSMENT');
    setPage(1);
    setSelectedFile(null);
    setLoading(false);
    closeModal({ modalName: modals.quickAction });
  };

  const update = (variables) =>
    updateModal({
      modalName: modals.quickAction,
      variables: { ...variables },
    });
  const typeOptions = useMemo(() => {
    const typeOptions = !!assessmentTypes
      ? Object.values(assessmentTypes)
          .map((t, index) => ({
            title: t?.title ?? t?.name,
            icon: t.icon,
            color: t.color,
            key: t.key,
            id: index,
            active: t.active,
          }))
          .filter(
            (t) =>
              t.active &&
              t.key !== 'OBSERVATION' &&
              t.key !== 'INCIDENT' &&
              t.key !== 'AUDIT',
          )
      : [];

    return typeOptions;
  }, [assessmentTypes]);

  const editedQuickAction = useMemo(() => {
    let newUrl = url;
    if (selectedType === 'ASSESSMENT') {
      newUrl = `/workspace/${
        allWorkspaces || quickActionType === quickActionTypes.companyLevel
          ? 'NO_WORKSPACE'
          : workspace?.id
      }/dashboard?&modalName=createAssessmentModal&type=${type?.key}`;
      if (asset?.id) {
        newUrl = newUrl.concat(`&asset=${asset.id}`);
      }
      if (template?.id) {
        newUrl = newUrl.concat(`&template=${template.id}`);
      }
    } else if (
      selectedType === 'LINK' &&
      !!url &&
      !url?.startsWith('http://') &&
      !url?.startsWith('https://')
    ) {
      newUrl = `https://${url}`;
    }

    return {
      color,
      name,
      icon,
      url: newUrl || selectedFile?.url,
      workspaceId: workspace?.id,
      template: template?.id,
      type: quickActionType,
    };
  }, [
    url,
    selectedType,
    color,
    name,
    icon,
    selectedFile?.url,
    workspace?.id,
    template?.id,
    quickActionType,
    allWorkspaces,
    type?.key,
    asset?.id,
  ]);

  const tab = getParam('tab');

  const optional = type?.key !== defaultAssessmentTypes.fleet.key;

  const { data: { workspaceVehicles = [] } = {} } = useQuery(modalQuery, {
    skip: !show || !workspace?.id,
    variables: {
      workspaceId: `${workspace?.id}`,
    },
  });

  const buttons = useMemo(
    () => [
      {
        title: assessmentName || t(keys.common.ASSESSMENT),
        icon: 'inventory',
        key: 'ASSESSMENT',
      },
      ...(!disableOutsideNavigation
        ? [
            {
              title: t(keys.common.LINK),
              icon: 'link',
              key: 'LINK',
            },
          ]
        : []),
      {
        title: 'Upload Document',
        icon: 'folder',
        key: 'DOC',
      },
      {
        title: 'Internal Document',
        icon: 'folder',
        key: 'INTERNAL',
      },
    ],
    [t, disableOutsideNavigation, assessmentName],
  );
  useEffect(() => {
    setSelectedFile(null);
  }, [selectedType]);

  const submitDisabled = useMemo(() => {
    if (page === 2) {
      switch (selectedType) {
        case 'DOC':
        case 'INTERNAL':
          return !selectedFile;
        case 'LINK':
          return !url;
        case 'ASSESSMENT':
          return !template;
        default:
          return false;
      }
    }
  }, [page, selectedFile, selectedType, template, url]);

  return (
    <Modal
      open={show}
      disableCloseOnSubmit={page === 1}
      disableCloseOnCancel={page === 2}
      onClose={() => {
        refetch();
        onHide();
        setPage(1);
      }}
      submitDisabled={submitDisabled}
      footer={
        !!tab &&
        tab === 'workspaces' &&
        page === 2 &&
        selectedType !== 'ASSESSMENT' && (
          <div className={styles.flex}>
            <Text noMargin weight="semiBold">
              Apply to all workspaces
            </Text>
            <Form.Check
              onClick={(e) => setAllWorkspaces(e.target.checked)}
              type="switch"
            />
          </div>
        )
      }
      titleImage={'⚡️'}
      title={`${
        quickActionType === quickActionTypes.companyLevel
          ? t(keys.settings.COMPANY)
          : quickActionType === quickActionTypes.workspaceLevel
          ? t(keys.common.WORKSPACE)
          : ''
      } ${t(keys.dashboard.CUSTOM_QUICK_ACTION)}`}
      submitText={page === 1 && t(keys.action.NEXT)}
      cancelText={page === 2 && t(keys.action.BACK)}
      onSubmit={async (e) => {
        e?.preventDefault?.();
        if (page === 1) {
          setPage(2);
          return;
        }
        try {
          const variables = allWorkspaces?.length
            ? { ...editedQuickAction, type: 'COMPANY' }
            : { ...editedQuickAction };

          await addQuickAction({ variables });
          await refetch();
        } catch (err) {
          console.error('submit error', err);
        }
      }}
      onCancel={() => {
        if (page === 2) {
          setPage(1);
        }
      }}
    >
      <div>
        {page === 1 ? (
          <div className={styles.container}>
            <div>
              <Text marginXs weight="semiBold">
                {t(keys.common.NAME)}
              </Text>
              <Form.Control
                placeholder={t(keys.action.ENTER, {
                  variable: t(keys.common.NAME),
                })}
                maxLength={30}
                value={name || ''}
                onChange={(e) => update({ name: e.target.value })}
              />
              <br />
              <IconModal onClick={(icon) => update({ icon })} />
            </div>
            <div className={styles.wrapper}>
              <div
                className={styles.buttonPreview}
                style={{ backgroundColor: color }}
              >
                <input
                  className={styles.colorPicker}
                  type="color"
                  value={color || ''}
                  onChange={(e) => update({ color: e.target.value })}
                />
                {!!icon && (
                  <Icon baseClassName="material-icons-outlined" color="white">
                    {`${icon}`}
                  </Icon>
                )}
                <Text
                  size={'sm'}
                  weight="semiBold"
                  textAlign="center"
                  color="white"
                  noMargin
                  className={!icon ? styles.iconText : styles.text}
                  truncate={!!icon}
                >
                  {name}
                </Text>
              </div>
            </div>
            <div>
              <Text textAlign="center" size="xl" noMargin>
                ⬆️
              </Text>
              <Text noMargin weight="semiBold" textAlign="center">
                {t(keys.dashboard.CLICK_TO_CHANGE_COLOR)}
              </Text>
            </div>
          </div>
        ) : (
          <div className={styles.page2}>
            <div className={styles.quickActionSelectionContainer}>
              {buttons.map(({ title, icon, key }) => (
                <div
                  className={classNames(
                    styles.containerLink,
                    selectedType === key ? styles.borderColor : null,
                  )}
                  onClick={() => {
                    setSelectedType(key);
                  }}
                >
                  <Icon
                    baseClassName="material-icons-outlined"
                    className={styles.icon}
                    fontSize="inherit"
                  >
                    {icon}
                  </Icon>
                  <Text size="md" weight="semiBold" textAlign="center" noMargin>
                    {title}
                  </Text>
                </div>
              ))}
            </div>
            <br />
            <div className={styles.paddingMd}>
              <Text size="sm" textAlign="center">
                {!!string && string()}
              </Text>
            </div>
            {selectedType === 'ASSESSMENT' && (
              <div className={styles.dropdownsContainer}>
                <TypeSelectorDropdown
                  onChange={(type) =>
                    update({
                      type,
                      template: null,
                      asset: null,
                    })
                  }
                  selected={type || defaultAssessmentTypes.fleet}
                  items={typeOptions}
                  type="Type"
                />
                {quickActionType === quickActionTypes.userLevel && (
                  <DropdownCombo
                    title={t(keys.common.WORKSPACE)}
                    preventStateChange
                    required
                    type={dropdownTypes.WORKSPACE}
                    items={inWorkspaces || []}
                    onChange={(workspace) =>
                      update({ workspace, asset: null, template: null })
                    }
                    selected={workspace}
                  />
                )}
                {quickActionType !== quickActionTypes.companyLevel && (
                  <DropdownCombo
                    type={dropdownTypes.ASSET}
                    title={t(keys.common.ASSET)}
                    items={workspaceVehicles || []}
                    onChange={(asset) => {
                      update({
                        asset,
                      });
                    }}
                    selected={asset}
                    optional={optional}
                    onRemove={() => update({ asset: null })}
                    required={type?.key === 'VEHICLE'}
                    highlight={type?.key === 'VEHICLE'}
                  />
                )}
                <DropdownCombo
                  type={dropdownTypes.TEMPLATE}
                  title={t(keys.common.TEMPLATE)}
                  highlight
                  preventStateChange
                  items={showTemplates || []}
                  onChange={(template) => {
                    update({
                      template,
                    });
                  }}
                  onRemove={() => update({ template: null })}
                  selected={template}
                />
                <br />
              </div>
            )}
            {selectedType === 'LINK' && (
              <>
                <div className={styles.flex}>
                  <Form.Control
                    placeholder="Enter URL"
                    value={url || ''}
                    onChange={(e) => {
                      update({ url: e.target.value });
                    }}
                    className={styles.urlInput}
                  />
                  <Button
                    value="Test"
                    onClick={() =>
                      window.open(
                        editedQuickAction.url,
                        '_blank',
                        'noopener,noreferrer',
                      )
                    }
                  />
                </div>
              </>
            )}

            {selectedFile ? (
              <div className={styles.fileInput}>
                <Text
                  hover
                  color="accentPrimary"
                  noMargin
                  textAlign="center"
                  weight="semiBold"
                  onClick={() => window.open(selectedFile.url)}
                >
                  {selectedFile?.name}
                </Text>
                <Icon hover color="red" onClick={() => setSelectedFile(null)}>
                  delete
                </Icon>
              </div>
            ) : selectedType === 'DOC' ? (
              <QuickFileDropzone
                loading={loading}
                setLoading={setLoading}
                onSubmit={(file) => {
                  setSelectedFile(file);
                  update({ url: file.url });
                }}
              />
            ) : (
              selectedType === 'INTERNAL' && (
                <div>
                  <Text noMargin weight="semiBold">
                    Select Internal File
                  </Text>
                  <div className={styles.spacer} />
                  <Select
                    options={fileOptions}
                    onChange={(file) => {
                      setSelectedFile(file);
                    }}
                  />
                  <br />
                  <br />
                </div>
              )
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
